import { useState } from 'react';
import { CancelSessionModal } from '@/components/ui/CancelSessionModal';
import { useCancelUserSession } from '@/services';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	AlertCircle,
	ArrowRight,
	CheckCircle2,
	MapPin,
	PlayCircle,
	Video,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar } from '@/components/ui/Avatar';
import { E2E_USER_HOME } from '@/constants/e2eSchedulingTestIds';
import { useUserSessionAccessWindow } from '@/hooks/useUserSessionAccessWindow';
import { formatProfessionalFullName, getSessionDisplayTitle } from '@/utils';
import { getImageUrl } from '@/utils/imageUrl';
import { openUserSessionCall } from '@/utils/userSessionCall';
import { toast } from 'react-toastify';

type UserHomeUpcomingSessionCardProps = {
	session: Sessions.Session;
	highlightBenefitPayment?: boolean;
	onPayment?: (session: Sessions.Session) => void;
};

type StatusVisual = {
	label: string;
	icon: ReactNode;
	badgeClassName: string;
};

const getSessionStatusVisual = (
	session: Sessions.Session,
	highlightBenefitPayment?: boolean,
): StatusVisual => {
	const isClubIncluded = !!session.included_in_club;
	const paymentsList =
		(session.payments as unknown as Sessions.PaymentDetails[]) || [];
	const hasBenefitApplied =
		!!highlightBenefitPayment &&
		paymentsList.some(
			(p) =>
				!!(p as unknown as { payment_benefit_application?: unknown })
					.payment_benefit_application,
		);

	if (isClubIncluded) {
		return {
			label: 'Serviço incluso no clube',
			icon: <CheckCircle2 size={14} />,
			badgeClassName: 'border-emerald-200 bg-emerald-50 text-emerald-700',
		};
	}

	if (session.status === 'STARTED') {
		return {
			label: 'Iniciada',
			icon: <PlayCircle size={14} />,
			badgeClassName: 'border-orange-200 bg-orange-100 text-orange-700',
		};
	}

	if (session.status === 'CREATED') {
		return {
			label: 'Aguardando pagamento',
			icon: <AlertCircle size={14} />,
			badgeClassName: 'border-amber-200 bg-amber-100 text-amber-700',
		};
	}

	if (session.status === 'CONFIRMED') {
		return {
			label: hasBenefitApplied ? 'Pago com benefício' : 'Pago',
			icon: <CheckCircle2 size={14} />,
			badgeClassName: 'border-emerald-200 bg-emerald-50 text-emerald-700',
		};
	}

	return {
		label: session.status,
		icon: <AlertCircle size={14} />,
		badgeClassName: 'border-stone-200 bg-stone-50 text-stone-600',
	};
};

const formatCalendarParts = (selectedDate: string) => {
	const dateObj = new Date(selectedDate);
	const monthRaw = dateObj.toLocaleString('pt-BR', { month: 'long' });
	const month = monthRaw.charAt(0).toUpperCase() + monthRaw.slice(1);
	const day = String(dateObj.getDate()).padStart(2, '0');

	const weekday = dateObj.toLocaleString('pt-BR', { weekday: 'long' });
	const weekdayLabel = weekday.charAt(0).toUpperCase() + weekday.slice(1);
	const time = dateObj.toLocaleTimeString('pt-BR', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});

	return {
		month,
		day,
		datePill: `${month} ${day}`,
		scheduleLabel: `${weekdayLabel}, às ${time}`,
	};
};

const getProfessionalServiceName = (session: Sessions.Session) => {
	const fromSession = session.service_name?.trim();
	if (fromSession) return fromSession;

	const services = session.professional?.professional_services ?? [];
	const matched = services.find(
		(item) => item.service?.id === session.service_id,
	);
	const fromMatch = matched?.service?.service_name?.trim();
	if (fromMatch) return fromMatch;

	const firstWithService = services.find(
		(item) => item.service?.service_name,
	);
	return firstWithService?.service?.service_name?.trim() || 'Psicologia';
};

const UserHomeUpcomingSessionCard = ({
	session,
	highlightBenefitPayment,
	onPayment,
}: UserHomeUpcomingSessionCardProps) => {
	const navigate = useNavigate();
	const [isJoiningCall, setIsJoiningCall] = useState(false);
	const [isCancelOpen, setIsCancelOpen] = useState(false);
	const { mutate: cancelUserSession, isPending: isCancelling } =
		useCancelUserSession(() => setIsCancelOpen(false));
	const canCancelSession =
		session.status === 'CONFIRMED' || session.status === 'CREATED';
	const isAccessUnlocked = useUserSessionAccessWindow(session);
	const canAccessSession =
		session.is_online &&
		(session.status === 'CONFIRMED' || session.status === 'STARTED');

	const { datePill, scheduleLabel } = formatCalendarParts(
		session.selected_date,
	);
	const sessionTitle = getSessionDisplayTitle(session);
	const statusVisual = getSessionStatusVisual(
		session,
		highlightBenefitPayment,
	);
	const professionalName = session.professional
		? formatProfessionalFullName(session.professional)
		: 'Profissional';
	const professionalService = getProfessionalServiceName(session);
	const professionalPhoto = session.professional?.profile_photo_url
		? getImageUrl(session.professional.profile_photo_url)
		: '';

	const handleAccess = async () => {
		if (!isAccessUnlocked) {
			toast.info('O acesso será liberado 5 minutos antes da sessão.');
			return;
		}

		await openUserSessionCall({
			sessionId: session.id,
			navigate,
			sessionStatus: session.status,
			onStart: () => setIsJoiningCall(true),
			onFinally: () => setIsJoiningCall(false),
		});
	};

	return (
		<article
			data-testid={`${E2E_USER_HOME.SESSION_CARD_PREFIX}${session.id}`}
			className="flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-stone-200 bg-white p-4 shadow-sm"
		>
			<div className="flex items-center gap-2 rounded-2xl border border-stone-200 bg-stone-50 px-2.5 py-2">
				<span className="shrink-0 rounded-full bg-brand-orange px-2.5 py-1 text-[10px] font-bold tracking-wide text-white">
					{datePill}
				</span>

				<p className="min-w-0 flex-1 truncate text-xs font-semibold text-stone-600 sm:text-sm">
					{scheduleLabel}
				</p>

				<span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full border border-stone-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-stone-500">
					{session.is_online ? (
						<Video size={12} aria-hidden />
					) : (
						<MapPin size={12} aria-hidden />
					)}
					{session.is_online ? 'Online' : 'Presencial'}
				</span>
			</div>

			<div className="flex items-center gap-3 pt-4">
				<div className="size-11 shrink-0 overflow-hidden rounded-full border border-stone-200 bg-stone-100">
					{professionalPhoto ? (
						<Avatar
							className="size-11 rounded-full"
							img_url={professionalPhoto}
							variant="soft"
						/>
					) : (
						<div className="flex size-full items-center justify-center bg-stone-200 text-xs font-bold text-stone-500">
							{professionalName.charAt(0)}
						</div>
					)}
				</div>
				<div className="min-w-0">
					<p className="truncate text-[10px] font-bold uppercase tracking-[0.14em] text-stone-400">
						{sessionTitle}
					</p>
					<p className="truncate text-base font-bold text-stone-900">
						{professionalName}
					</p>
					<p className="truncate text-xs font-medium text-stone-500">
						{professionalService}
					</p>
				</div>
			</div>

			<div className="mt-auto pt-3">
				<div className="flex flex-wrap items-center gap-2">
					<div
						className={cn(
							'inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold',
							statusVisual.badgeClassName,
						)}
					>
						{statusVisual.icon}
						<span className="truncate leading-tight">
							{statusVisual.label}
						</span>
					</div>

					{session.status === 'CREATED' ? (
						<button
							type="button"
							data-testid={`${E2E_USER_HOME.SESSION_PAY_PREFIX}${session.id}`}
							onClick={() => onPayment?.(session)}
							className="ml-auto inline-flex min-w-[8.5rem] flex-1 items-center justify-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-700 sm:flex-initial"
						>
							Pagar
							<ArrowRight size={14} strokeWidth={2.5} />
						</button>
					) : canAccessSession ? (
						<button
							type="button"
							onClick={handleAccess}
							disabled={isJoiningCall || !isAccessUnlocked}
							className={cn(
								'ml-auto inline-flex min-w-[8.5rem] flex-1 items-center justify-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white transition-colors sm:flex-initial',
								isJoiningCall || !isAccessUnlocked
									? 'cursor-not-allowed bg-stone-300'
									: 'bg-stone-900 hover:bg-stone-800',
							)}
						>
							{isJoiningCall
								? 'Acessando...'
								: isAccessUnlocked
								  ? 'Acessar'
								  : 'Disponível 5 min antes'}
							<ArrowRight size={14} strokeWidth={2.5} />
						</button>
					) : null}
				</div>
				{canCancelSession && (
					<button
						type="button"
						data-testid={`user-home-cancel-${session.id}`}
						onClick={() => setIsCancelOpen(true)}
						className="mt-2 text-xs font-semibold text-stone-400 underline-offset-2 transition-colors hover:text-rose-600 hover:underline"
					>
						Cancelar consulta
					</button>
				)}
			</div>
			<CancelSessionModal
				isOpen={isCancelOpen}
				onClose={() => setIsCancelOpen(false)}
				onConfirm={(reason) => {
					if (isCancelling) return;
					cancelUserSession({ id: session.id, reason });
				}}
				session={session}
				actor="user"
				isPending={isCancelling}
			/>
		</article>
	);
};

const UserHomeUpcomingSessionCardSkeleton = () => (
	<div className="flex animate-pulse flex-col overflow-hidden rounded-[1.75rem] border border-stone-200 bg-white p-4 shadow-sm">
		<div className="flex items-center gap-2 rounded-2xl border border-stone-200 bg-stone-50 px-2.5 py-2">
			<div className="h-6 w-14 shrink-0 rounded-full bg-stone-200" />
			<div className="h-4 flex-1 rounded bg-stone-200" />
			<div className="ml-auto h-6 w-16 shrink-0 rounded-full bg-stone-200" />
		</div>
		<div className="flex items-center gap-3 pt-4">
			<div className="size-11 rounded-full bg-stone-200" />
			<div className="flex-1 space-y-2">
				<div className="h-3 w-16 rounded bg-stone-200" />
				<div className="h-4 w-32 rounded bg-stone-200" />
				<div className="h-3 w-24 rounded bg-stone-200" />
			</div>
		</div>
		<div className="mt-auto pt-3">
			<div className="flex items-center justify-between gap-2">
				<div className="h-6 w-28 rounded-full bg-stone-200" />
				<div className="h-8 w-24 rounded-full bg-stone-200" />
			</div>
		</div>
	</div>
);

export { UserHomeUpcomingSessionCard, UserHomeUpcomingSessionCardSkeleton };
